import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Truck, Trash, FilePdf, Eye } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Transport Invoices — list page for the specialised transport document
 * type. Visible only to users whose super_admin has flipped
 * `transport_invoice_enabled` on; the sidebar entry is otherwise hidden.
 */
export default function TransportInvoices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tiMonth, setTiMonth] = useState("");
  const [tiFrom, setTiFrom] = useState("");
  const [tiTo, setTiTo] = useState("");
  const navigate = useNavigate();

  const visibleRows = useMemo(() => rows.filter((r) => {
    const d = r.invoice_date || "";
    if (tiMonth) return d.startsWith(tiMonth);
    if (tiFrom && d < tiFrom) return false;
    if (tiTo && d > tiTo) return false;
    return true;
  }), [rows, tiMonth, tiFrom, tiTo]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/transport-invoices`);
      setRows(r.data || []);
      setError("");
    } catch (e) {
      // 404 == feature not enabled for this user. Redirect them home.
      if (e?.response?.status === 404) {
        navigate("/dashboard", { replace: true });
        return;
      }
      setError(e?.response?.data?.detail || "Failed to load transport invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleDelete = async (id, no) => {
    if (!window.confirm(`Delete transport invoice ${no}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/transport-invoices/${id}`);
      setRows(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const downloadPdf = async (id, no) => {
    try {
      const r = await axios.get(`${API}/transport-invoices/${id}/pdf`, { responseType: "blob" });
      // Guard: some proxies return HTML error pages with 200 — verify the
      // blob type before treating it as a PDF.
      const type = r.data.type || "";
      if (!type.includes("pdf") && r.data.size < 200) {
        throw new Error("Server did not return a PDF");
      }
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Try to parse a backend error out of the blob if the server sent
      // one, otherwise show the status + generic detail.
      let msg = e?.message || "Failed to download PDF";
      const blob = e?.response?.data;
      if (blob && typeof blob.text === "function") {
        try { const t = await blob.text(); const j = JSON.parse(t); msg = j.detail || t; } catch (_) { /* ignore */ }
      }
      const status = e?.response?.status;
      alert(`Could not download PDF${status ? ` (HTTP ${status})` : ""}: ${msg}`);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="transport-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2 text-slate-500 text-xs font-bold uppercase tracking-widest">
            <Truck size={14} weight="fill" /> Transporter Module
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Transport Invoices</h1>
          <p className="text-sm text-slate-500 mt-1">
            Specialised billing for transport jobs — CN No · Route · HSN · Amount.
          </p>
        </div>
        <Link
          to="/transport-invoices/create"
          data-testid="new-ti-btn"
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-sm text-sm font-bold tracking-wide transition-all"
        >
          <Plus size={18} weight="bold" /> New Transport Invoice
        </Link>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-sm bg-rose-50 border border-rose-200 text-rose-700 text-sm">{error}</div>
      )}

      <div className="mb-4 flex items-end gap-2 flex-wrap" data-testid="ti-date-filters">
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500">Month</label>
          <input type="month" data-testid="ti-month-filter" value={tiMonth} onChange={(e) => { setTiMonth(e.target.value); if (e.target.value) { setTiFrom(""); setTiTo(""); } }} className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500">From</label>
          <input type="date" data-testid="ti-from-filter" value={tiFrom} onChange={(e) => { setTiFrom(e.target.value); if (e.target.value) setTiMonth(""); }} className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500">To</label>
          <input type="date" data-testid="ti-to-filter" value={tiTo} onChange={(e) => { setTiTo(e.target.value); if (e.target.value) setTiMonth(""); }} className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        {(tiMonth || tiFrom || tiTo) && (
          <>
            <button data-testid="ti-date-clear" onClick={() => { setTiMonth(""); setTiFrom(""); setTiTo(""); }} className="px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-100">Clear</button>
            <span className="text-xs font-semibold text-slate-500 pb-2">{visibleRows.length} of {rows.length} shown</span>
          </>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <div className="p-12 text-center">
            <Truck size={40} weight="duotone" className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-semibold mb-1">{rows.length === 0 ? "No transport invoices yet" : "No transport invoices in the selected period"}</p>
            {rows.length === 0 && <p className="text-slate-500 text-sm">Create your first one to get started.</p>}
          </div>
        ) : (
          <div className="overflow-x-auto sticky-table">
            <table className="min-w-full text-sm">
              <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  <th className="px-4 py-3 text-left">Bill No</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Party</th>
                  <th className="px-4 py-3 text-right">Items</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleRows.map((r) => (
                  <tr key={r.id} data-testid={`ti-row-${r.id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-bold text-slate-900">{r.invoice_no}</td>
                    <td className="px-4 py-3 text-slate-700">{r.invoice_date}</td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 font-semibold">{r.party_name}</div>
                      {r.party_gstin && (
                        <div className="text-[10px] text-slate-400 tracking-wide">{r.party_gstin}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{(r.items || []).length}</td>
                    <td className="px-4 py-3 text-right font-bold text-slate-900">
                      ₹{Number(r.total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/transport-invoices/${r.id}`}
                          data-testid={`ti-view-${r.id}`}
                          className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
                          title="View"
                        ><Eye size={18} /></Link>
                        <button
                          onClick={() => downloadPdf(r.id, r.invoice_no)}
                          data-testid={`ti-pdf-${r.id}`}
                          className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
                          title="Download PDF"
                        ><FilePdf size={18} /></button>
                        <button
                          onClick={() => handleDelete(r.id, r.invoice_no)}
                          data-testid={`ti-delete-${r.id}`}
                          className="p-2 text-slate-600 hover:text-rose-700 hover:bg-rose-50 rounded-sm"
                          title="Delete"
                        ><Trash size={18} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
